// src/pages/ApplicationChampionshipMatches/components/MatchesTable/MatchesTable.tsx
import {
  Badge,
  Box,
  Flex,
  Icon,
  IconButton,
  Image,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Text,
} from '@chakra-ui/react';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';

import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { CustomTable } from '@/components/CustomTable/CustomTable';
import { MatchStatus, MatchV2, translateMatchStatus } from '@/model';
import { formatDateToDateTimeDisplay } from '@/utils/formatDateToDateTimeDisplay/formatDateToDateTimeDisplay';

const I18N_PREFIX = 'pages.application_championship_matches.components.matches_table';

/** Patch parcial aceito pelo handler de auto-save inline. */
export type InlinePatch = Partial<
  Pick<MatchV2, 'home_goals' | 'away_goals' | 'status' | 'match_date'>
>;

export interface MatchesTableProps {
  data: MatchV2[];
  isLoading: boolean;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  sorting?: { field: string; desc: boolean };
  onSortingChange?: (field: string, desc: boolean) => void;
  onPaginationChange: (page: number, pageSize: number) => void;
  onEditClick: (item: MatchV2) => void;
  onDeleteClick: (item: MatchV2) => void;
  /** Auto-save inline. Recebe a row + patch e dispara PUT. */
  onInlineSave: (item: MatchV2, patch: InlinePatch) => void;
}

/**
 * Converte um Date/ISO string em valor aceito por <input type="datetime-local">
 * (YYYY-MM-DDTHH:mm, sempre em horário local).
 */
const toDateTimeLocal = (value?: string): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
};

const ScoreCell = ({
  row,
  onInlineSave,
}: {
  row: MatchV2;
  onInlineSave: MatchesTableProps['onInlineSave'];
}) => {
  const [home, setHome] = useState<string>(
    row.home_goals !== undefined && row.home_goals !== null ? String(row.home_goals) : '',
  );
  const [away, setAway] = useState<string>(
    row.away_goals !== undefined && row.away_goals !== null ? String(row.away_goals) : '',
  );

  useEffect(() => {
    setHome(row.home_goals !== undefined && row.home_goals !== null ? String(row.home_goals) : '');
    setAway(row.away_goals !== undefined && row.away_goals !== null ? String(row.away_goals) : '');
  }, [row.home_goals, row.away_goals]);

  const handleNumericChange =
    (setter: typeof setHome) => (event: ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value;
      if (next === '' || /^\d{0,2}$/.test(next)) setter(next);
    };

  const saveGoals = (field: 'home_goals' | 'away_goals', value: string) => {
    const original = field === 'home_goals' ? row.home_goals : row.away_goals;
    const parsed = value === '' ? null : Number(value);
    if (parsed === (original ?? null)) return;
    onInlineSave(row, { [field]: parsed === null ? undefined : parsed });
  };

  return (
    <Flex alignItems='center' gap={2}>
      <Input
        type='text'
        textAlign='center'
        value={home}
        onChange={handleNumericChange(setHome)}
        onBlur={() => saveGoals('home_goals', home)}
        bg='white'
        borderColor='gray.300'
        borderRadius={8}
        width='64px'
        fontSize='sm'
        inputMode='numeric'
        maxLength={2}
      />
      <ChakraFeatherIcon name='x' />
      <Input
        type='text'
        textAlign='center'
        value={away}
        onChange={handleNumericChange(setAway)}
        onBlur={() => saveGoals('away_goals', away)}
        bg='white'
        borderColor='gray.300'
        borderRadius={8}
        width='64px'
        fontSize='sm'
        inputMode='numeric'
        maxLength={2}
      />
    </Flex>
  );
};

const StatusCell = ({
  row,
  onInlineSave,
}: {
  row: MatchV2;
  onInlineSave: MatchesTableProps['onInlineSave'];
}) => {
  const [status, setStatus] = useState<MatchStatus>(row.status);

  useEffect(() => {
    setStatus(row.status);
  }, [row.status]);

  return (
    <Select
      value={status}
      onChange={event => {
        const next = event.target.value as MatchStatus;
        setStatus(next);
        if (next !== row.status) onInlineSave(row, { status: next });
      }}
      bg='white'
      borderColor='gray.300'
      borderRadius={8}
      size='sm'
      minW='150px'
    >
      {Object.values(MatchStatus).map(value => (
        <option key={value} value={value}>
          {translateMatchStatus(value)}
        </option>
      ))}
    </Select>
  );
};

const DateCell = ({
  row,
  onInlineSave,
}: {
  row: MatchV2;
  onInlineSave: MatchesTableProps['onInlineSave'];
}) => {
  const [value, setValue] = useState<string>(toDateTimeLocal(row.match_date));

  useEffect(() => {
    setValue(toDateTimeLocal(row.match_date));
  }, [row.match_date]);

  // Só edita data quando a partida está agendada. Depois de RUNNING/FINISHED
  // a data é um fato histórico — qualquer correção passa pelo modal de edição
  // completa, não por auto-save inline.
  const isEditable = row.status === MatchStatus.SCHEDULED;

  if (!isEditable) {
    return (
      <Text fontSize='sm' color='gray.700' minW='180px'>
        {formatDateToDateTimeDisplay(row.match_date ?? null)}
      </Text>
    );
  }

  const saveDate = () => {
    const nextIso = value ? new Date(value).toISOString() : undefined;
    if (nextIso === row.match_date) return;
    onInlineSave(row, { match_date: nextIso });
  };

  return (
    <Input
      type='datetime-local'
      value={value}
      onChange={event => setValue(event.target.value)}
      onBlur={saveDate}
      bg='white'
      borderColor='gray.300'
      borderRadius={8}
      size='sm'
      minW='180px'
    />
  );
};

const CompetitionCell = ({ row }: { row: MatchV2 }) => {
  const stage = row.stage;
  const championship = stage?.season?.championship;
  return (
    <Flex direction='column' gap={1} minW='180px'>
      <Text fontSize='sm' fontWeight='medium' noOfLines={1}>
        {championship?.name ?? '-'}
        {stage?.season?.year ? ` · ${stage.season.year}` : ''}
      </Text>
      {stage ? (
        <Badge
          variant='subtle'
          colorScheme='purple'
          textTransform='none'
          borderRadius='full'
          px={2}
          alignSelf='flex-start'
        >
          {stage.group_name ? `${stage.name} · ${stage.group_name}` : stage.name}
        </Badge>
      ) : null}
    </Flex>
  );
};

const MatchCell = ({
  row,
  onInlineSave,
}: {
  row: MatchV2;
  onInlineSave: MatchesTableProps['onInlineSave'];
}) => (
  <Flex alignItems='center' gap={2} minW='340px'>
    {row.home_team?.logo_path ? (
      <Image
        src={row.home_team.logo_path}
        alt={row.home_team.name}
        w='24px'
        h='24px'
        objectFit='contain'
      />
    ) : null}
    <Text fontWeight='bold' noOfLines={1} flex='1' textAlign='right'>
      {row.home_team?.name ?? '-'}
    </Text>
    <ScoreCell row={row} onInlineSave={onInlineSave} />
    <Text fontWeight='bold' noOfLines={1} flex='1'>
      {row.away_team?.name ?? '-'}
    </Text>
    {row.away_team?.logo_path ? (
      <Image
        src={row.away_team.logo_path}
        alt={row.away_team.name}
        w='24px'
        h='24px'
        objectFit='contain'
      />
    ) : null}
  </Flex>
);

export const MatchesTable = ({
  data,
  isLoading,
  pageNumber,
  pageSize,
  totalPages,
  totalItems,
  sorting,
  onSortingChange,
  onPaginationChange,
  onEditClick,
  onDeleteClick,
  onInlineSave,
}: MatchesTableProps) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<MatchV2>();

  const columns = [
    columnHelper.display({
      id: 'competition',
      header: t(`${I18N_PREFIX}.col_competition`),
      cell: (props: CellContext<MatchV2, unknown>) => (
        <CompetitionCell row={props.row.original} />
      ),
      enableSorting: false,
    }),
    columnHelper.accessor('round', {
      cell: props => props.getValue() ?? '-',
      header: t(`${I18N_PREFIX}.col_round`),
    }),
    columnHelper.accessor('match_date', {
      header: t(`${I18N_PREFIX}.col_date`),
      cell: props => <DateCell row={props.row.original} onInlineSave={onInlineSave} />,
    }),
    columnHelper.display({
      id: 'match',
      header: t(`${I18N_PREFIX}.col_match`),
      cell: props => <MatchCell row={props.row.original} onInlineSave={onInlineSave} />,
      enableSorting: false,
      meta: { align: 'center' },
    }),
    columnHelper.display({
      id: 'status',
      header: t(`${I18N_PREFIX}.col_status`),
      cell: props => <StatusCell row={props.row.original} onInlineSave={onInlineSave} />,
      enableSorting: false,
    }),
    columnHelper.display({
      id: 'actions',
      header: t(`${I18N_PREFIX}.col_actions`),
      cell: props => (
        <Flex justifyContent='end'>
          <Menu>
            <MenuButton
              as={IconButton}
              textColor='text.table'
              variant='ghost'
              size='sm'
              icon={<Icon as={EllipsisVerticalIcon} />}
              aria-label={t(`${I18N_PREFIX}.menu_aria_label`)}
            />
            <MenuList>
              <MenuItem
                onClick={() => onEditClick(props.row.original)}
                icon={
                  <Box>
                    <ChakraFeatherIcon name='edit-2' />
                  </Box>
                }
              >
                {t(`${I18N_PREFIX}.action_edit`)}
              </MenuItem>
              <MenuItem
                onClick={() => onDeleteClick(props.row.original)}
                icon={
                  <Box>
                    <ChakraFeatherIcon name='trash-2' color='red.500' />
                  </Box>
                }
              >
                {t(`${I18N_PREFIX}.action_delete`)}
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      ),
      enableSorting: false,
      meta: { align: 'right' },
    }),
  ];

  return (
    <CustomTable
      data={data}
      isLoading={isLoading}
      pageNumber={pageNumber}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      columns={columns}
      onPaginationChange={onPaginationChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
};
